/**
 * adOnlyScope.js — Pure persistence helpers for the Checks page's "Ad
 * traffic only" scope switch (docs/checks-adscope-spec.md §4, WP1).
 *
 * Default is ON. The ONLY value that persists as OFF is the literal string
 * 'false' — written when a user explicitly flips the switch off. Anything
 * else read back from storage (absent key, 'true', a corrupted/legacy
 * value) resolves to ON, so a first visit, a cleared localStorage, or
 * garbage data all fail open to the safer ad-scoped default rather than
 * silently reverting to the noisier all-traffic view.
 */

export const AD_ONLY_STORAGE_KEY = 'checks.adOnly';

/**
 * resolveAdOnlyFromStored(stored) -> boolean
 * Pure — takes whatever localStorage.getItem() would return (string or
 * null) and resolves the switch's boolean state. No I/O.
 */
export function resolveAdOnlyFromStored(stored) {
  return stored !== 'false';
}

/**
 * readAdOnlyPreference() -> boolean
 * Reads the persisted preference from localStorage. Never throws — any
 * access failure (privacy mode, disabled storage, etc.) resolves to the ON
 * default.
 */
export function readAdOnlyPreference() {
  try {
    return resolveAdOnlyFromStored(localStorage.getItem(AD_ONLY_STORAGE_KEY));
  } catch {
    return true;
  }
}

/**
 * writeAdOnlyPreference(adOnly) -> void
 * Persists the switch state. Never throws.
 */
export function writeAdOnlyPreference(adOnly) {
  try {
    localStorage.setItem(AD_ONLY_STORAGE_KEY, adOnly ? 'true' : 'false');
  } catch {
    // ignore — best-effort persistence only
  }
}
